import { useState } from "react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { X, Plus, Loader2, Sparkles, Target, Trophy, AlertTriangle } from "lucide-react";

/**
 * Dialog per creare uno sponsor custom della lega (non piattaforma).
 * Fields: name, tagline, description, category (bonus/paid), upfront/prize/penalty credits,
 * objective_desc + logo_url (opzionale — se assente, verrà mostrato solo il nome).
 */
export default function SponsorCreateDialog({ leagueId, onClose, onCreated }) {
  const [category, setCategory] = useState("bonus_initial");
  const [name, setName] = useState("");
  const [tagline, setTagline] = useState("");
  const [description, setDescription] = useState("");
  const [logoUrl, setLogoUrl] = useState("");
  const [uploading, setUploading] = useState(false);
  const [upfront, setUpfront] = useState(50);
  const [prize, setPrize] = useState(80);
  const [penalty, setPenalty] = useState(0);
  const [objective, setObjective] = useState("");
  const [busy, setBusy] = useState(false);

  const uploadLogo = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 3 * 1024 * 1024) { toast.error("File troppo grande (max 3MB)"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      fd.append("scope", "sponsor");
      const r = await api.post("/uploads/logo", fd, { headers: { "Content-Type": "multipart/form-data" }});
      setLogoUrl(r.data.url);
      toast.success("Logo caricato");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setUploading(false); }
  };

  const isBonus = category === "bonus_initial";
  const accent = isBonus ? "#10b981" : "#f59e0b";

  const canSubmit = name.trim().length >= 2 && prize > 0 && !!objective.trim();

  const submit = async () => {
    if (!canSubmit) {
      toast.error("Compila almeno nome, obiettivo e premio base > 0");
      return;
    }
    setBusy(true);
    try {
      const payload = {
        category,
        name: name.trim(),
        tagline: tagline.trim() || null,
        description: description.trim() || null,
        logo_url: logoUrl.trim() || null,
        upfront_credits: Number(upfront) || 0,
        prize_credits: Number(prize) || 0,
        penalty_credits: Number(penalty) || 0,
        objective_type: "manual",
        objective_desc: objective.trim(),
        active: true,
        is_platform: false,
        prize_scale: [
          { position_max: 1, percent: 150 },
          { position_max: 3, percent: 130 },
          { position_max: 6, percent: 100 },
          { position_max: 8, percent: 70 },
        ],
        default_percent: 50,
      };
      const r = await api.post(`/leagues/${leagueId}/sponsors`, payload);
      toast.success(`✅ Sponsor "${r.data.name}" creato!`);
      onCreated?.(r.data);
      onClose?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur"
      data-testid="sponsor-create-dialog"
      onClick={(e) => { if (e.target === e.currentTarget && !busy) onClose?.(); }}>
      <div className="w-full max-w-2xl max-h-[92vh] overflow-hidden bg-slate-900 border border-slate-700 rounded-2xl shadow-2xl flex flex-col">
        {/* Header */}
        <div className="px-5 py-4 border-b border-slate-800 flex items-center justify-between"
          style={{ background: `linear-gradient(90deg, ${accent}22, transparent)` }}>
          <div className="flex items-center gap-2">
            <Sparkles style={{ color: accent }} size={18}/>
            <div>
              <div className="font-black uppercase tracking-[0.2em] text-white text-sm">Nuovo sponsor personalizzato</div>
              <div className="text-[10px] text-slate-400">Disponibile solo per la tua lega</div>
            </div>
          </div>
          <button onClick={onClose} disabled={busy} className="text-slate-400 hover:text-white disabled:opacity-30" data-testid="close-create">
            <X size={18}/>
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          {/* Category */}
          <div>
            <label className="block text-[10px] uppercase tracking-[0.2em] text-slate-400 font-bold mb-2">
              Tipo sponsor
            </label>
            <div className="grid grid-cols-2 gap-2">
              {["bonus_initial", "manager_paid"].map(c => (
                <button key={c} onClick={() => setCategory(c)} disabled={busy}
                  data-testid={`cat-${c}`}
                  className={`p-3 rounded-xl border-2 transition-all ${
                    category === c
                      ? c === "bonus_initial" ? "border-emerald-500 bg-emerald-500/10" : "border-amber-500 bg-amber-500/10"
                      : "border-slate-700 bg-slate-950/50 hover:border-slate-500"
                  }`}>
                  <div className={`text-xs font-black uppercase tracking-widest ${
                    c === "bonus_initial" ? "text-emerald-300" : "text-amber-300"
                  }`}>
                    {c === "bonus_initial" ? "Bonus" : "Pagato"}
                  </div>
                  <div className="text-[10px] text-slate-400 mt-0.5">
                    {c === "bonus_initial" ? "Dà crediti al team all'accettazione" : "Costa al team all'accettazione"}
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Name + Tagline */}
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-[10px] uppercase tracking-[0.2em] text-slate-400 font-bold mb-1">
                Nome sponsor *
              </label>
              <input type="text" value={name} onChange={e => setName(e.target.value)} maxLength={60}
                data-testid="input-sponsor-name"
                disabled={busy} placeholder="Es. Pizzeria Da Ciro"
                className="w-full px-3 py-2 rounded-lg bg-slate-950 border border-slate-700 text-white text-sm"/>
            </div>
            <div>
              <label className="block text-[10px] uppercase tracking-[0.2em] text-slate-400 font-bold mb-1">
                Tagline (opzionale)
              </label>
              <input type="text" value={tagline} onChange={e => setTagline(e.target.value)} maxLength={80}
                data-testid="input-sponsor-tagline"
                disabled={busy} placeholder="Es. La margherita più buona del quartiere"
                className="w-full px-3 py-2 rounded-lg bg-slate-950 border border-slate-700 text-white text-sm"/>
            </div>
          </div>

          {/* Description */}
          <div>
            <label className="block text-[10px] uppercase tracking-[0.2em] text-slate-400 font-bold mb-1">
              Descrizione (opzionale)
            </label>
            <textarea value={description} onChange={e => setDescription(e.target.value)} rows={2} maxLength={200}
              data-testid="input-sponsor-desc"
              disabled={busy} placeholder="Es. Ristorante che offre premi in pizza gratis"
              className="w-full px-3 py-2 rounded-lg bg-slate-950 border border-slate-700 text-white text-sm resize-none"/>
          </div>

          {/* Logo upload */}
          <div>
            <label className="block text-[10px] uppercase tracking-[0.2em] text-slate-400 font-bold mb-1">
              Logo (opzionale)
            </label>
            <div className="flex items-center gap-3">
              <div className="w-16 h-16 rounded-lg bg-slate-950 border border-slate-700 flex items-center justify-center overflow-hidden shrink-0">
                {logoUrl
                  ? <img src={logoUrl} alt="preview" className="max-w-full max-h-full object-contain"/>
                  : <span className="text-[9px] uppercase tracking-widest text-slate-600">no logo</span>}
              </div>
              <div className="flex-1 space-y-1.5">
                <input type="file" accept="image/*" onChange={uploadLogo} disabled={busy || uploading}
                  data-testid="input-sponsor-logo-file"
                  className="text-xs text-slate-300 file:mr-3 file:py-1 file:px-3 file:rounded-lg file:border-0 file:bg-emerald-500 file:text-slate-900 file:font-black file:cursor-pointer"/>
                <input type="text" value={logoUrl} onChange={e => setLogoUrl(e.target.value)} maxLength={500}
                  data-testid="input-sponsor-logo"
                  disabled={busy || uploading} placeholder="…oppure incolla URL"
                  className="w-full px-2 py-1 rounded-lg bg-slate-950 border border-slate-700 text-white text-[10px] font-mono"/>
                {uploading && <div className="text-[10px] text-slate-500">Upload in corso…</div>}
              </div>
            </div>
          </div>

          {/* Obiettivo */}
          <div>
            <label className="text-[10px] uppercase tracking-[0.2em] text-slate-400 font-bold mb-1 flex items-center gap-1">
              <Target size={11} style={{ color: accent }}/> Obiettivo *
            </label>
            <textarea value={objective} onChange={e => setObjective(e.target.value)} rows={2} maxLength={200}
              data-testid="input-sponsor-obj"
              disabled={busy}
              placeholder='Es. "Bomber — Segnare almeno 40 gol in campionato"'
              className="w-full px-3 py-2 rounded-lg bg-slate-950 border border-slate-700 text-white text-sm resize-none"
              style={{ borderColor: objective ? `${accent}55` : undefined }}/>
            <p className="text-[10px] text-slate-500 mt-1">
              Chi validerà? A fine stagione l&apos;admin conferma manualmente se l&apos;obiettivo è stato centrato.
            </p>
          </div>

          {/* Crediti */}
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="block text-[10px] uppercase tracking-[0.2em] text-slate-400 font-bold mb-1">
                {isBonus ? "Bonus subito (+)" : "Costo subito (−)"}
              </label>
              <input type="number" min={0} step={5} value={upfront} onChange={e => setUpfront(Math.max(0, parseInt(e.target.value) || 0))}
                data-testid="input-upfront"
                disabled={busy}
                className="w-full px-3 py-2 rounded-lg bg-slate-950 border border-slate-700 text-white text-lg font-mono font-black text-center"/>
            </div>
            <div>
              <label className="block text-[10px] uppercase tracking-[0.2em] text-slate-400 font-bold mb-1">
                Premio base *
              </label>
              <input type="number" min={0} step={5} value={prize} onChange={e => setPrize(Math.max(0, parseInt(e.target.value) || 0))}
                data-testid="input-prize"
                disabled={busy}
                className="w-full px-3 py-2 rounded-lg bg-slate-950 border border-slate-700 text-white text-lg font-mono font-black text-center"/>
              <div className="text-[9px] text-slate-500 mt-1 text-center font-mono">
                {Math.round(prize * 0.5)}→{Math.round(prize * 1.5)}c
              </div>
            </div>
            <div>
              <label className="block text-[10px] uppercase tracking-[0.2em] text-slate-400 font-bold mb-1">
                Penalità (se mancato)
              </label>
              <input type="number" min={0} step={5} value={penalty} onChange={e => setPenalty(Math.max(0, parseInt(e.target.value) || 0))}
                data-testid="input-penalty"
                disabled={busy}
                className="w-full px-3 py-2 rounded-lg bg-slate-950 border border-slate-700 text-white text-lg font-mono font-black text-center"/>
            </div>
          </div>

          {/* Preview */}
          <div className="rounded-xl bg-slate-950/60 border p-3" style={{ borderColor: `${accent}44` }}>
            <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-slate-400 mb-2 flex items-center gap-1">
              <Trophy size={11} style={{ color: accent }}/> Preview crediti per piazzamento
            </div>
            <div className="grid grid-cols-5 gap-1.5 text-[10px]">
              {[
                { pos: "1°", pct: 150 },
                { pos: "≤3°", pct: 130 },
                { pos: "≤6°", pct: 100 },
                { pos: "≤8°", pct: 70 },
                { pos: "altri", pct: 50 },
              ].map((t, i) => (
                <div key={i} className="rounded-lg bg-slate-900 border border-slate-800 p-1.5 text-center">
                  <div className="text-slate-500 font-mono">{t.pos}</div>
                  <div className="font-black text-sm mt-0.5" style={{ color: accent }}>{Math.round(prize * t.pct / 100)}c</div>
                  <div className="text-slate-600 font-mono text-[9px]">{t.pct}%</div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between gap-2 px-5 py-4 border-t border-slate-800 bg-slate-950/50">
          <div className="text-[10px] text-slate-500 uppercase tracking-widest">* campi obbligatori</div>
          <div className="flex items-center gap-2">
            <button onClick={onClose} disabled={busy}
              className="px-3 py-1.5 rounded-lg text-xs font-bold text-slate-300 border border-slate-700 hover:border-slate-500 disabled:opacity-30"
              data-testid="cancel-create">Annulla</button>
            <button onClick={submit} disabled={busy || !canSubmit}
              data-testid="submit-create-sponsor"
              className={`inline-flex items-center gap-1.5 px-4 py-1.5 rounded-lg text-xs font-black uppercase tracking-[0.2em] text-slate-900 disabled:opacity-30 shadow-lg ${
                isBonus
                  ? "bg-gradient-to-br from-emerald-400 to-emerald-600 shadow-emerald-500/20"
                  : "bg-gradient-to-br from-amber-400 to-amber-600 shadow-amber-500/20"
              }`}>
              {busy ? <><Loader2 className="animate-spin" size={13}/> Creo…</>
                : <><Plus size={13}/> Crea sponsor</>}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
